import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, AlertCircle, Clock, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface SyncStatusBadgeProps {
  status?: 'pending' | 'syncing' | 'synced' | 'failed' | 'local_only';
  syncError?: string;
  className?: string;
  showLabel?: boolean;
}

export const SyncStatusBadge = ({
  status,
  syncError,
  className,
  showLabel = true
}: SyncStatusBadgeProps) => {
  const { t } = useTranslation();

  if (!status || status === 'local_only') {
    if (!showLabel) return null;

    return (
      <Badge
        variant="outline"
        className={cn("text-xs", className)}
        title={t("calendar.sync.localOnly", "Local")}
      >
        <Minus className="h-3 w-3 mr-1" />
        {t("calendar.sync.localOnly", "Local")}
      </Badge>
    );
  }

  switch (status) {
    case 'pending':
      return (
        <Badge
          variant="outline"
          className={cn("text-xs text-blue-500 border-blue-200 bg-blue-50", className)}
          title={t("calendar.sync.pending", "Pending")}
        >
          <Clock className="h-3 w-3 mr-1" />
          {showLabel && t("calendar.sync.pending", "Pending")}
        </Badge>
      );

    case 'syncing':
      return (
        <Badge
          variant="outline"
          className={cn("text-xs text-blue-500 border-blue-200 bg-blue-50", className)}
          title={t("calendar.sync.syncing", "Syncing...")}
        >
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          {showLabel && t("calendar.sync.syncing", "Syncing...")}
        </Badge>
      );

    case 'synced':
      return (
        <Badge
          variant="outline"
          className={cn("text-xs text-green-600 border-green-200 bg-green-50", className)}
          title={t("calendar.sync.synced", "Synced")}
        >
          <CheckCircle2 className="h-3 w-3 mr-1" />
          {showLabel && t("calendar.sync.synced", "Synced")}
        </Badge>
      );

    case 'failed':
      return (
        <Badge
          variant="outline"
          className={cn("text-xs text-red-600 border-red-200 bg-red-50", className)}
          title={syncError || t("calendar.sync.failed", "Sync Failed")}
        >
          <AlertCircle className="h-3 w-3 mr-1" />
          {showLabel && t("calendar.sync.failed", "Sync Failed")}
        </Badge>
      );

    default:
      return null;
  }
};
